import { createDomain, sample } from "effector";
import { createArticles } from "../../entities/articles/api";
import { articlesEntityModel } from "../../entities/articles/model";
import { campaignDetailsEntityModel } from "../../entities/campaigns/model/details";
import { isValidUrl } from "../../entities/articles/utils/is-valid-url";
import { notifications } from "@mantine/notifications";

const domain = createDomain();

const getUniqueArticles = (articles: InputArticle[]) => {
  const map = new Map();
  return articles.filter((article) => {
    if (map.has(article.url)) {
      return false;
    }
    map.set(article.url, true);
    return true;
  });
};

export type InputArticle = {
  url: string;
  date?: Date;
  skipCutLongSentences?: boolean;
};

const open = domain.createEvent();
const close = domain.createEvent();
const $opened = domain
  .createStore(false)
  .on(open, () => true)
  .on(close, () => false);

const create = domain.createEvent();

const createFx = domain.createEffect(createArticles);

const reset = domain.createEvent();

const $addedArticles = domain.createStore<InputArticle[]>([]).reset(reset);

const removeArticle = domain.createEvent<InputArticle["url"]>();

const addArticles = domain.createEvent<InputArticle[]>();

sample({
  clock: create,
  source: {
    articles: $addedArticles,
    campaignId: campaignDetailsEntityModel.$currentCampaignId,
  },
  filter: ({ campaignId, articles }) =>
    Boolean(campaignId) && articles.length > 0,
  fn: ({ articles, campaignId }) => ({
    campaignId: campaignId!,
    articles,
  }),
  target: createFx,
});

sample({
  clock: create,
  source: {
    articles: $addedArticles,
  },
  filter: ({ articles }) => articles.length === 0,
  fn: () => {
    notifications.show({
      message: "Add at least one article to the campaign",
      color: "red",
    });
  },
});

sample({
  clock: addArticles,
  source: $addedArticles,
  fn: (articles, payload) => {
    const newArticles = [...articles, ...payload];
    const validArticles = newArticles.filter((article) =>
      isValidUrl(article.url)
    );
    return getUniqueArticles(validArticles);
  },
  target: $addedArticles,
});

sample({
  clock: removeArticle,
  source: $addedArticles,
  fn: (articles, url) => articles.filter((article) => article.url !== url),
  target: $addedArticles,
});

sample({
  clock: createFx.doneData,
  target: [
    articlesEntityModel.getList,
    campaignDetailsEntityModel.getCampaignDetails,
  ],
});

createFx.doneData.watch(() => {
  notifications.show({
    title: "Articles created",
    message: "Articles created successfully",
  });
});

sample({
  clock: createFx.doneData,
  target: close,
});

domain.onCreateStore((store) => store.reset(close));

export const createArticleModel = {
  createFx,
  $addedArticles,
  reset,
  removeArticle,
  addArticles,
  create,
  $opened,
  open,
  close,
};
